import { useEffect, useMemo, useState } from 'react';
import { useAuthStore, useChatInputStore } from '@/store';
import useComposerText from '@/hooks/useComposerText';
import { shouldSuggestWorkMode, MIN_QUERY_LENGTH } from '@/constants/utils/work_mode_intent_matcher';

const DEBOUNCE_MS = 220;
const EMPTY_FILES = [];

/**
 * Debounced Work-mode intent for live composer text + attachments.
 * Same cadence as useToolIntentSuggestions.
 */
export default function useWorkModeIntentSuggestions(contextId) {
  const message = useComposerText(contextId);
  const workModeSuggestionsEnabled = useAuthStore(
    state => state.user?.flags?.isPlus === true
      && state.user?.flags?.isGhost !== true
      && state.user?.preferences?.chat?.workModeSuggestionsEnabled !== false
  );
  const files = useChatInputStore((state) => {
    const key = contextId != null && contextId !== ''
      ? contextId
      : (state.currentContextId || 'new');
    return state.inputStates[key]?.files ?? EMPTY_FILES;
  });
  const [debouncedMessage, setDebouncedMessage] = useState('');
  const [debouncedFiles, setDebouncedFiles] = useState(EMPTY_FILES);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedMessage(message || '');
      setDebouncedFiles(Array.isArray(files) ? files : EMPTY_FILES);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [message, files]);

  const shouldSuggest = useMemo(() => {
    if (!workModeSuggestionsEnabled) return false;
    const trimmed = (debouncedMessage || '').trim();
    const fileCount = Array.isArray(debouncedFiles) ? debouncedFiles.length : 0;
    if (trimmed.length < MIN_QUERY_LENGTH && fileCount < 2) return false;
    return shouldSuggestWorkMode(debouncedMessage, { files: debouncedFiles });
  }, [debouncedMessage, debouncedFiles, workModeSuggestionsEnabled]);

  return {
    shouldSuggest,
    message,
    files,
    debouncedMessage,
    debouncedFiles,
  };
}
